import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ReceiptText } from "lucide-react";
import { OverviewSettings } from "@/components/settings/OverviewSettings";
import { BillingPlansSettings } from "@/components/settings/BillingPlansSettings";
import UserProfileSettings from "@/components/settings/UserProfileSettings";
import EmailSettingsAdmin from "@/components/settings/EmailSettingsAdmin";
import { SubscriptionModal } from "@/components/billing/SubscriptionModal";
import { useIsAdmin } from "@/hooks/useUserRole";

type SettingsTab = "overview" | "profile" | "email" | "billing";

const VALID_TABS: SettingsTab[] = ["overview", "profile", "email", "billing"];

const TAB_TRIGGER_CLASSES =
  "rounded-none border-b-2 border-transparent bg-transparent px-1 pb-3 pt-2 text-sm font-medium text-gray-500 shadow-none transition-colors hover:text-gray-800 data-[state=active]:border-purple-600 data-[state=active]:bg-transparent data-[state=active]:text-purple-700 data-[state=active]:shadow-none";

const Settings: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isAdmin = useIsAdmin();

  const tabParam = searchParams.get("tab") as SettingsTab | null;
  const upgradeParam = searchParams.get("upgrade");
  const activeTab: SettingsTab =
    tabParam && VALID_TABS.includes(tabParam)
      ? tabParam
      : upgradeParam
      ? "billing"
      : "overview";

  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [targetUpgradePlan, setTargetUpgradePlan] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (upgradeParam) {
      setTargetUpgradePlan(upgradeParam);
      setIsUpgradeModalOpen(true);
    }
  }, [upgradeParam]);

  const handleTabChange = (nextTab: string) => {
    const params = new URLSearchParams(searchParams);
    params.set("tab", nextTab);
    setSearchParams(params, { replace: true });
  };

  const handleOpenUpgradeModal = (planName?: string) => {
    setTargetUpgradePlan(planName || "Starter");
    setIsUpgradeModalOpen(true);
  };

  const handleModalOpenChange = (open: boolean) => {
    setIsUpgradeModalOpen(open);
    if (!open && searchParams.has("upgrade")) {
      const params = new URLSearchParams(searchParams);
      params.delete("upgrade");
      setSearchParams(params, { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <header className="border-b border-gray-200 bg-white">
          <div className="mx-auto max-w-8xl px-4 pt-8 md:px-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
                <p className="mt-1 text-gray-500">
                  Manage your account, team and application preferences.
                </p>
              </div>
              <Button variant="outline" onClick={() => handleTabChange("billing")}>
                <ReceiptText className="mr-2 h-4 w-4" />
                Billing History
              </Button>
            </div>
            <TabsList className="mt-6 h-auto w-full justify-start gap-6 rounded-none bg-transparent p-0">
              <TabsTrigger value="overview" className={TAB_TRIGGER_CLASSES}>
                Overview
              </TabsTrigger>
              <TabsTrigger value="profile" className={TAB_TRIGGER_CLASSES}>
                Profile
              </TabsTrigger>
              {isAdmin && (
                <TabsTrigger value="email" className={TAB_TRIGGER_CLASSES}>
                  Email
                </TabsTrigger>
              )}
              <TabsTrigger value="billing" className={TAB_TRIGGER_CLASSES}>
                Billing
              </TabsTrigger>
            </TabsList>
          </div>
        </header>

        <main className="mx-auto max-w-8xl px-4 py-8 md:px-8">
          <TabsContent value="overview" className="mt-0">
            <OverviewSettings onUpgrade={() => handleOpenUpgradeModal()} />
          </TabsContent>
          <TabsContent value="profile" className="mt-0">
            <UserProfileSettings />
          </TabsContent>
          {isAdmin && (
            <TabsContent value="email" className="mt-0">
              <EmailSettingsAdmin />
            </TabsContent>
          )}
          <TabsContent value="billing" className="mt-0">
            <BillingPlansSettings onSelectPlan={handleOpenUpgradeModal} />
          </TabsContent>
        </main>
      </Tabs>

      <SubscriptionModal
        open={isUpgradeModalOpen}
        onOpenChange={handleModalOpenChange}
        targetPlanName={targetUpgradePlan}
      />
    </div>
  );
};

export default Settings;
